import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Grid, Typography, InputBase, MenuItem, Popover, Button } from '@quillbot/design-system';
import { Add, DeleteOutline, ArrowDropDown } from '@mui/icons-material';
import { capitalize } from 'core/utils';
import { getCgBreakpointDown } from 'src/citation-generator/utils';
import {
  AuthhorKeys,
  CitationSourceEnum,
  Contributor,
  ContributorDetails,
  ContributorTypes,
} from 'src/citation-generator/types';
import { useEditFormDispatch, useEditFormState } from '../context';
import { setContributors, updateContributor } from '../actionCreators';
import { getDefaultContributorState } from '../utils';

const useAutherTextWithLabelStyle = makeStyles((theme) => ({
  addAuthorBtn: {
    borderColor: theme.palette.divider,
  },
  contributorsContainer: {
    [getCgBreakpointDown(theme)]: {
      paddingRight: ({ hasDelete }: { hasDelete?: boolean }) => (hasDelete ? '22px' : undefined),
    },
  },
  inputContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 3,
    width: 'calc(30% - 18px)',
    height: '30px',
    [getCgBreakpointDown(theme)]: {
      width: '100%',
      marginBottom: 10,
    },
  },
  suffixInput: {
    width: '75px',
    marginBottom: 8,
    [getCgBreakpointDown(theme)]: {
      width: '100%',
    },
  },
  input: {
    padding: '0px 10px',
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    width: '100%',
  },
  label: {
    float: 'right',
    fontSize: '12px',
    color: theme.palette.text.primary,
  },
  labelContainer: {
    width: '24%',
    paddingRight: '8px',
    [theme.breakpoints.down(437)]: {
      paddingRight: '0px',
    },
  },
  dropDownText: {
    fontSize: '12px',
    fontWeight: 600,
  },
  warning: {
    border: `1px solid ${theme.palette.secondary.main}`,
  },
  deleteIcon: {
    position: 'absolute',
    right: '-20px',
    top: '8px',
    fontSize: '15px',
    cursor: 'pointer',
    color: '#5F6368',
    [getCgBreakpointDown(theme)]: {
      right: 0,
    },
  },
}));

interface OnChange {
  onChange: (key: AuthhorKeys, value: string) => void;
}

const ContributorFields = (
  props: ContributorDetails & OnChange & { isEdit?: boolean; hasDelete?: boolean }
) => {
  const { suffix, firstName, middleName, lastName, onChange, isEdit, hasDelete, initials, title } =
    props;
  const classes = useAutherTextWithLabelStyle({ hasDelete });
  const highlightFirstName = isEdit && !(firstName || '');
  const highlightLastName = isEdit && !(lastName || '');

  return (
    <Grid container justifyContent="space-between" className={classes.contributorsContainer}>
      <Grid item className={`${classes.inputContainer} ${classes.suffixInput}`}>
        <InputBase
          className={classes.input}
          placeholder="Title"
          value={title}
          onChange={(e) => {
            onChange('title', e.target.value);
          }}
        />
      </Grid>

      <Grid item style={{}} className={classes.inputContainer}>
        <InputBase
          className={classes.input}
          placeholder="Initials"
          value={initials}
          onChange={(e) => {
            onChange('initials', e.target.value);
          }}
        />
      </Grid>

      <Grid
        item
        style={{}}
        className={`${classes.inputContainer} ${highlightFirstName ? classes.warning : ''}`}
      >
        <InputBase
          className={classes.input}
          placeholder="First Name"
          value={firstName || ''}
          onChange={(e) => {
            onChange('firstName', e.target.value);
          }}
          // onBlur={(e) => {
          //   if (!e.target.value && isEdit) setHighlightFirstName(true);
          // }}
        />
      </Grid>

      {/*
      <Grid item style={{}} className={classes.inputContainer}>
        <InputBase
          className={classes.input}
          placeholder="Middle Name"
          value={middleName}
          onChange={(e) => {
            onChange('middleName', e.target.value);
          }}
        />
      </Grid>
      */}

      <Grid
        item
        style={{}}
        className={`${classes.inputContainer} ${highlightLastName ? classes.warning : ''}`}
      >
        <InputBase
          className={classes.input}
          placeholder="Last Name"
          value={lastName || ''}
          onChange={(e) => {
            onChange('lastName', e.target.value);
            // if (e.target.value) setHighlightLastName(false);
          }}
          // onBlur={(e) => {
          //   if (!e.target.value && isEdit) setHighlightLastName(true);
          // }}
        />
      </Grid>

      {/*
      <Grid item className={`${classes.inputContainer} ${classes.suffixInput}`}>
        <InputBase
          className={classes.input}
          placeholder="Suffix"
          value={suffix}
          onChange={(e) => {
            onChange('suffix', e.target.value);
          }}
        />
      </Grid>
      */}
    </Grid>
  );
};

function ContributorTextFieidWithLabel(props: {
  rootClass?: any;
  contributor: Contributor;
  onContributorChange: Function;
  isEdit?: boolean;
  deleteContributor?: Function;
}) {
  const { contributor, onContributorChange, deleteContributor } = props;
  const classes = useAutherTextWithLabelStyle({});
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const sourceType = useEditFormState((state) => state.sourceType);

  return (
    <>
      <Grid container alignItems="center" className={props.rootClass}>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          elevation={4}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {Object.values(ContributorTypes).map((type) => (
            <MenuItem
              style={{
                fontSize: '12px',
                display:
                  type === ContributorTypes.COMPILERS && sourceType === CitationSourceEnum.WEBSITE
                    ? 'none'
                    : undefined,
              }}
              key={type}
              disabled={type === contributor.contributorType}
              onClick={() => {
                onContributorChange({ ...contributor, contributorType: type as ContributorTypes });
                setAnchorEl(null);
              }}
            >
              {capitalize(type)}
            </MenuItem>
          ))}
        </Popover>

        <Grid
          item
          className={classes.labelContainer}
          onClick={(e) => {
            setAnchorEl(e.target as HTMLDivElement);
          }}
          style={{
            alignSelf: 'start',
            marginTop: 5,
            cursor: 'pointer',
          }}
        >
          <Typography className={classes.label}>
            <Grid container alignItems="center">
              <Typography className={classes.dropDownText}>
                {capitalize(contributor.contributorType)}
              </Typography>

              <ArrowDropDown
                fontSize="small"
                style={{
                  transform: anchorEl ? 'rotate(180deg)' : '',
                  transition: 'transform 0.2s',
                }}
                sx={(theme) => ({
                  [theme.breakpoints.down(437)]: {
                    width: '13px',
                  },
                })}
              />
            </Grid>
          </Typography>
        </Grid>

        <Grid container style={{ width: '76%', position: 'relative' }}>
          {deleteContributor && (
            <DeleteOutline
              className={classes.deleteIcon}
              onClick={() => {
                deleteContributor();
              }}
            />
          )}

          <ContributorFields
            suffix={contributor.suffix}
            firstName={contributor.firstName}
            // middleName={contributor.middleName}
            lastName={contributor.lastName}
            initials={contributor.initials}
            title={contributor.title}
            isEdit={props.isEdit}
            onChange={(key, keyValue) => {
              onContributorChange({ ...contributor, [key]: keyValue });
            }}
            hasDelete={Boolean(deleteContributor)}
          />
        </Grid>
      </Grid>
    </>
  );
}

export function ContributorField() {
  const classes = useAutherTextWithLabelStyle({});
  const contributors = useEditFormState((state) => state.contributors) || [];
  const dispatch = useEditFormDispatch();
  const isEdit = useEditFormState((state) => state.isEdit);

  return (
    <Grid item container>
      {contributors?.map((contributor, index) => (
        <ContributorTextFieidWithLabel
          key={contributor.key || `${index}-contributor`}
          contributor={contributor}
          isEdit={isEdit}
          onContributorChange={(newContributor: Contributor) => {
            dispatch(updateContributor(index, newContributor));
          }}
          deleteContributor={
            index !== 0
              ? () => {
                  const preContributors = [...contributors];
                  preContributors.splice(index, 1);
                  dispatch(setContributors(preContributors));
                }
              : undefined
          }
        />
      ))}

      <Grid container>
        <Grid style={{ width: '24%' }} />

        <Button
          className={classes.addAuthorBtn}
          startIcon={<Add />}
          variant="outlined"
          size="small"
          color="inherit"
          onClick={() => {
            dispatch(setContributors([...contributors, getDefaultContributorState()]));
          }}
          style={{
            fontWeight: 600,
          }}
        >
          Add
        </Button>
      </Grid>
    </Grid>
  );
}
